
import { Options, Vue } from "vue-class-component";
import MButton from "@/components/MButton.vue";
import MIcon from "@/components/MIcon.vue";
import MLoadingIcon from "@/components/MLoadingIcon.vue";
import MTable from "@/components/MTable.vue";
import MNavBar from "@/components/MNavBar.vue";
import MHeaderPanel from "@/components/MHeaderPanel.vue";
import { State } from "@/store/helper";
import { Student } from "@/entities/student";
import { School } from "@/entities/school";
import { TutorMeta } from "@/api/tutor";
import {
  HeartRateAlertSetting,
  heartRateAlertSettingCollectionKey,
  HeartRateAlertSettingData
} from "@/entities/heart_rate_alert_setting";
import dayjs from "dayjs";
import { heartRateAlertSettingsTableDefinition } from "@/utils/table";
import HearRateAlertSettingAddModal from "@/components/hear_rate_alert_setting/HeartRateAlertSettingAddModal.vue";
import HeartRateAlertSettingEditModal from "@/components/hear_rate_alert_setting/HeartRateAlertSettingEditModal.vue";
import store from "@/store";
import {
  createHeartRateAlertSetting,
  deleteHeartRateAlertSetting,
  getHeartRateAlertSettings,
  getHeartRateAlertSettingOfStudent,
  updateHeartRateAlertSetting
} from "@/api/heart_rate_alert_setting";

type HeartRateAlertSettingForList = {
  //テーブル用のフィールドを型に追加
  id: string;
  title: string;
  createdTimeStamp: string;
  targetStudents: string;
};

@Options({
  components: {
    MButton,
    MIcon,
    MLoadingIcon,
    MTable,
    MNavBar,
    MHeaderPanel,
    HearRateAlertSettingAddModal,
    HeartRateAlertSettingEditModal
  }
})
export default class HeartRateAlertSettingList extends Vue {
  @State("students") students?: Student[];
  @State("school") school?: School;
  @State("tutor") tutor?: TutorMeta;
  @State("allStudentsFetched") allStudentsFetched?: boolean;

  tableDefinitions = heartRateAlertSettingsTableDefinition;
  selectedSettingIds: string[] = [];

  loading = false;
  searchKeyword = "";
  heartRateAlertSettings: HeartRateAlertSetting[] = [];

  addModalShowing = false;
  editModalShowing = false;
  selectedSetting: HeartRateAlertSetting | null = null;

  get filteredHeartRateAlertSettingsForTable(): HeartRateAlertSettingForList[] {
    if (!this.searchKeyword) return this.heartRateAlertSettingsForTable;
    return this.heartRateAlertSettingsForTable.filter(_ =>
      _.title.includes(this.searchKeyword)
    );
  }

  get heartRateAlertSettingsForTable(): HeartRateAlertSettingForList[] {
    return [...this.heartRateAlertSettings]
      .sort((a, b) => a.data.createdAt - b.data.createdAt)
      .map(data => this.convertToHeartRateAlertSettingForList(data));
  }

  get validStudents(): Student[] {
    if (!this.school) return [];
    if (!this.students) return [];

    return this.students.filter(
      student =>
        student.ref.path.split("/").includes(this.school!.ref.id) &&
        !!student.data.polarDeviceId
    );
  }

  convertToHeartRateAlertSettingForList(
    setting: HeartRateAlertSetting
  ): HeartRateAlertSettingForList {
    return {
      id: setting.ref.id,
      title: setting.data.title,
      createdTimeStamp: dayjs
        .unix(setting.data.createdAt)
        .format("YYYY/MM/DD HH:mm"),
      targetStudents: this.allStudentsFetched
        ? (setting.data.targetStudentRefs ?? [])
            .map(
              ref =>
                (this.students ?? []).find(s => s.ref.id === ref.id)?.data
                  .name ?? "-"
            )
            .join(", ")
        : "読み込み中..."
    };
  }

  onSelectionChange(settingId: string) {
    if (this.selectedSettingIds.includes(settingId)) {
      this.selectedSettingIds = this.selectedSettingIds.filter(
        id => id !== settingId
      );
    } else {
      this.selectedSettingIds.push(settingId);
    }
  }

  onSelectedForEdit(settingId: string) {
    const setting = this.heartRateAlertSettings.find(
      s => s.ref.id === settingId
    );
    if (!setting) return;

    this.selectedSetting = setting;
    this.editModalShowing = true;
  }

  showAddModal() {
    this.addModalShowing = true;
  }

  showEditModal() {
    this.editModalShowing = true;
  }

  async executeAdd(
    data: Omit<HeartRateAlertSettingData, "createdAt" | "createdBy">
  ) {
    if (!this.school) return;
    if (!this.tutor) return;
    if (!this.allStudentsFetched) return;

    try {
      store.commit("START_LOADING", "登録中...");

      const studentIds = (
        await Promise.all(
          (data.targetStudentRefs ?? []).map(async ref => {
            const existing = await getHeartRateAlertSettingOfStudent(
              this.school!,
              ref
            );
            if (existing) return ref.id;
            return null;
          })
        )
      ).filter(_ => _ !== null);

      if (studentIds.length > 0) {
        alert(
          `すでに使用されている生徒がいます。\n${studentIds
            .map(
              id => this.students?.find(s => s.ref.id === id)?.data.name ?? "-"
            )
            .join(", ")}`
        );
        return;
      }

      const newSetting = await createHeartRateAlertSetting(
        {
          ...data,
          threshold: +data.threshold,
          alertLevels: data.alertLevels.map(level => ({
            ...level,
            minimumDurationSec: +level.minimumDurationSec
          })),
          targetStudentCount: data.targetStudentRefs?.length ?? 0,
          createdAt: dayjs().unix(),
          createdBy: this.tutor.main.ref.id
        },
        this.school
      );

      this.heartRateAlertSettings.push(newSetting);
      this.addModalShowing = false;
    } catch (e) {
      console.error(e);
      alert(
        `心拍アラートの登録に失敗しました\n${
          e instanceof Error ? e.message : "unknown error"
        }`
      );
    } finally {
      store.commit("END_LOADING");
    }
  }

  async executeEdit(
    data: Omit<HeartRateAlertSettingData, "createdAt" | "createdBy">
  ) {
    if (!this.school) return;
    if (!this.selectedSetting) return;
    if (!this.allStudentsFetched) return;

    try {
      store.commit("START_LOADING", "更新中...");

      const currentStudentIds = new Set(
        (this.selectedSetting.data.targetStudentRefs ?? []).map(ref => ref.id)
      );

      // 新規追加された生徒のみチェック
      const addedStudentRefs = (data.targetStudentRefs ?? []).filter(
        ref => !currentStudentIds.has(ref.id)
      );

      const studentIds = (
        await Promise.all(
          addedStudentRefs.map(async ref => {
            const existing = await getHeartRateAlertSettingOfStudent(
              this.school!,
              ref
            );
            if (existing && existing.ref.id !== this.selectedSetting!.ref.id)
              return ref.id;
            return null;
          })
        )
      ).filter(_ => _ !== null);

      if (studentIds.length > 0) {
        alert(
          `すでに使用されている生徒がいます。\n${studentIds
            .map(
              id => this.students?.find(s => s.ref.id === id)?.data.name ?? "-"
            )
            .join(", ")}`
        );
        return;
      }

      await updateHeartRateAlertSetting(
        {
          title: data.title,
          threshold: +data.threshold,
          alertLevels: data.alertLevels.map(level => ({
            ...level,
            minimumDurationSec: +level.minimumDurationSec
          })),
          targetStudentCount: data.targetStudentRefs?.length ?? 0
        },
        this.selectedSetting.ref
      );

      const index = this.heartRateAlertSettings.findIndex(
        s => s.ref.id === this.selectedSetting!.ref.id
      );
      if (index !== -1) {
        this.heartRateAlertSettings[index] = {
          ...this.selectedSetting,
          data: {
            ...this.selectedSetting.data,
            title: data.title,
            threshold: +data.threshold,
            alertLevels: data.alertLevels.map(level => ({
              ...level,
              minimumDurationSec: +level.minimumDurationSec
            })),
            targetStudentRefs: data.targetStudentRefs
          }
        };
      }

      this.editModalShowing = false;
      this.selectedSetting = null;
    } catch (e) {
      console.error(e);
      alert(
        `心拍アラートの更新に失敗しました\n${
          e instanceof Error ? e.message : "unknown error"
        }`
      );
    } finally {
      store.commit("END_LOADING");
    }
  }

  async deleteSelectedSetting(id: string) {
    const setting = this.heartRateAlertSettings.find(s => s.ref.id === id);
    if (!setting) return;

    if (!window.confirm("設定を削除しますか？")) {
      return;
    }

    try {
      store.commit("START_LOADING", "削除中...");
      await deleteHeartRateAlertSetting(setting.ref);

      this.heartRateAlertSettings = this.heartRateAlertSettings.filter(
        setting => setting.ref.id !== id
      );

      this.editModalShowing = false;
      this.selectedSetting = null;
    } catch (e) {
      console.error(e);
      alert(
        `削除に失敗しました。\n${
          e instanceof Error ? e.message : "unknown error"
        }`
      );
    } finally {
      store.commit("END_LOADING");
    }
  }

  async deleteSelectedSettings() {
    if (!this.selectedSettingIds.length) return;
    if (!this.school) return;

    if (!window.confirm("選択した設定を削除しますか？")) {
      return;
    }

    try {
      store.commit("START_LOADING", "削除中...");

      await Promise.all(
        this.selectedSettingIds.map(id =>
          deleteHeartRateAlertSetting(
            this.school!.ref.collection(heartRateAlertSettingCollectionKey).doc(
              id
            )
          )
        )
      );

      this.heartRateAlertSettings = this.heartRateAlertSettings.filter(
        setting => !this.selectedSettingIds.includes(setting.ref.id)
      );
      this.selectedSettingIds = [];
    } catch (e) {
      console.error(e);
      alert(
        `削除に失敗しました。\n${
          e instanceof Error ? e.message : "unknown error"
        }`
      );
    } finally {
      store.commit("END_LOADING");
    }
  }

  async created(): Promise<void> {
    if (!this.school) return;

    this.loading = true;
    try {
      this.heartRateAlertSettings = await getHeartRateAlertSettings(
        this.school
      );
    } catch (error) {
      alert(
        "心拍アラートの取得に失敗しました" +
          "\n" +
          (error instanceof Error ? error.message : "unknown error")
      );
      console.error("Error when fetching settings", error);
    } finally {
      this.loading = false;
    }
  }
}
